export type BottomSheetBehaviour = {
  dismissible?: boolean;
  onClose: () => void;
};

/** Past this far down, or this fast, the release dismisses instead of springing
 * back. The distance is also capped as a fraction of the sheet in `onPointerUp`
 * so a tall sheet does not demand an unreachable drag. */
const DISMISS_DISTANCE = 72;
const DISMISS_VELOCITY = 0.5;
// Upward over-drag. The sheet sits on `bottom: 0`, so lifting it exposes the
// backdrop underneath; OVERDRAG_MAX is the asymptote and the stylesheet paints a
// skirt of the sheet's own colour that is deeper than it, so the gap never shows.
// OVERDRAG_SOFTNESS sets how quickly resistance builds: the first pixels move
// nearly 1:1 and the hundredth barely moves at all.
const OVERDRAG_MAX = 16;
const OVERDRAG_SOFTNESS = 48;

const isInteractive = (target: EventTarget | null) => (target as Element | null)?.closest('a, button, input, select, textarea, [role="button"], [role="menuitem"], [contenteditable]') !== null;

/** A drag that starts inside content the user has scrolled is a scroll, not a
 * dismissal. Only a sheet already at its top may be pulled down. */
const isScrolledContent = (target: EventTarget | null, sheet: HTMLElement) => {
  let node = target as Element | null;
  while (node && node !== sheet) {
    if (node.scrollHeight > node.clientHeight && node.scrollTop > 0) return true;
    node = node.parentElement;
  }
  return sheet.scrollHeight > sheet.clientHeight && sheet.scrollTop > 0;
};

// Bound as listeners rather than in markup: a <dialog> is not an interactive
// element, so a click handler in markup claims an interactivity it lacks.
// Escape needs no handler at all — the platform closes a modal <dialog> and
// that fires `close`, which is already wired to onClose.
export const delegateBottomSheet = (sheet: HTMLDialogElement, { dismissible = true, onClose }: BottomSheetBehaviour) => {
  let drag: { id: number; startY: number; startedAt: number; offset: number } | null = null;

  const handleClick = (event: MouseEvent) => {
    if (!dismissible) return;
    // A click synthesised from the keyboard reports 0,0, which is outside the
    // sheet's rect and would read as a backdrop click on every Enter press.
    if (event.detail === 0) return;
    // Hit-test the rect rather than compare targets: the sheet carries padding
    // of its own, and a target comparison treats a click on that padding as a
    // click on the backdrop.
    const rect = sheet.getBoundingClientRect();
    const inside = event.clientX >= rect.left && event.clientX <= rect.right && event.clientY >= rect.top && event.clientY <= rect.bottom;
    if (!inside) onClose();
  };

  const handleCancel = (event: Event) => {
    if (!dismissible) event.preventDefault();
  };

  // A sheet dismissed by dragging keeps the offset it was released at; the
  // next open must start from the bottom rather than part-way up.
  const handleClose = () => {
    sheet.style.translate = '';
    delete sheet.dataset.dragging;
    onClose();
  };

  const handlePointerDown = (event: PointerEvent) => {
    if (!dismissible || event.button !== 0) return;
    // A drag already owns this sheet. Without this guard a second finger landing
    // mid-drag re-seats `startY` to its own position, and the next move event
    // jumps the sheet by the distance between the two pointers.
    if (drag) return;
    if (isInteractive(event.target) || isScrolledContent(event.target, sheet)) return;
    drag = { id: event.pointerId, offset: 0, startedAt: event.timeStamp, startY: event.clientY };
    sheet.setPointerCapture(event.pointerId);
    sheet.dataset.dragging = '';
  };

  const handlePointerMove = (event: PointerEvent) => {
    const held = drag;
    if (!held || held.id !== event.pointerId) return;
    // Downward tracks the finger exactly. Upward is damped rather than clamped:
    // a hard stop at 0 reads as an invisible wall, while rising resistance reads
    // as the sheet already being against its edge. The divisor grows with the
    // distance dragged, so the surface gives less the harder it is pulled.
    const raw = event.clientY - held.startY;
    held.offset = raw >= 0 ? raw : -OVERDRAG_MAX * (1 - Math.exp(raw / OVERDRAG_SOFTNESS));
    sheet.style.translate = `0 ${held.offset}px`;
  };

  const handlePointerUp = (event: PointerEvent) => {
    const held = drag;
    if (!held || held.id !== event.pointerId) return;
    drag = null;
    if (sheet.hasPointerCapture(event.pointerId)) sheet.releasePointerCapture(event.pointerId);
    delete sheet.dataset.dragging;

    const elapsed = Math.max(1, event.timeStamp - held.startedAt);
    const far = held.offset > Math.min(DISMISS_DISTANCE, sheet.getBoundingClientRect().height * 0.4);
    const fast = held.offset / elapsed > DISMISS_VELOCITY && held.offset > 16;
    if (far || fast) {
      onClose();
      return;
    }
    sheet.style.translate = '';
  };

  sheet.addEventListener('click', handleClick);
  sheet.addEventListener('cancel', handleCancel);
  sheet.addEventListener('close', handleClose);
  sheet.addEventListener('pointerdown', handlePointerDown);
  sheet.addEventListener('pointermove', handlePointerMove);
  sheet.addEventListener('pointerup', handlePointerUp);
  sheet.addEventListener('pointercancel', handlePointerUp);
  return () => {
    sheet.removeEventListener('click', handleClick);
    sheet.removeEventListener('cancel', handleCancel);
    sheet.removeEventListener('close', handleClose);
    sheet.removeEventListener('pointerdown', handlePointerDown);
    sheet.removeEventListener('pointermove', handlePointerMove);
    sheet.removeEventListener('pointerup', handlePointerUp);
    sheet.removeEventListener('pointercancel', handlePointerUp);
  };
};
